'use client'

import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { motion, AnimatePresence } from 'framer-motion'
import { ChevronDown, Check } from 'lucide-react'
import { getFlagByCode } from '@/components/ui/flags'

const languages = [
  { code: 'en', name: 'English' },
  { code: 'es', name: 'Español' },
  { code: 'fr', name: 'Français' },
  { code: 'sv', name: 'Svenska' },
  { code: 'de', name: 'Deutsch' },
  { code: 'id', name: 'Bahasa Indonesia' },
  { code: 'ru', name: 'Русский' },
  { code: 'zh-CN', name: '简体中文' },
]

interface LanguagePickerProps {
  className?: string
  style?: React.CSSProperties
  compact?: boolean
}

export default function LanguagePicker({ className = '', style = {}, compact = false }: LanguagePickerProps) {
  const { i18n } = useTranslation()
  const [isOpen, setIsOpen] = useState(false)

  const currentLanguage = languages.find(lang => lang.code === i18n.language) || languages[0]
  const CurrentFlag = getFlagByCode(currentLanguage.code)

  const handleLanguageChange = async (languageCode: string) => {
    try {
      if (!i18n.hasResourceBundle(languageCode, 'translation')) {
        const res = await fetch(`/locales/${languageCode}.json`)
        if (res.ok) {
          const data = await res.json()
          i18n.addResourceBundle(languageCode, 'translation', data, true, true)
        }
      }
    } catch (err) {
      console.warn('Failed to load locale bundle dynamically:', err)
    }
    await i18n.changeLanguage(languageCode)
    setIsOpen(false)
  }

  return (
    <div className={`relative ${className}`} style={style}>
      <motion.button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-1.5 sm:px-3.5 sm:py-2 rounded-xl bg-white/5 backdrop-blur-md border border-white/10 hover:border-white/20 hover:bg-white/10 transition-all duration-200 shadow-sm"
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        style={{ color: '#fff' }}
      >
        <span className="inline-flex items-center shadow-sm">
          <CurrentFlag className="w-5 h-3.5 rounded-sm" />
        </span>
        {!compact && (
          <span className="text-xs sm:text-sm font-semibold opacity-90 hidden sm:inline">
            {currentLanguage.name}
          </span>
        )}
        <ChevronDown size={13} className={`opacity-50 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
      </motion.button>

      <AnimatePresence>
        {isOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-[60]"
              onClick={() => setIsOpen(false)}
            />

            {/* Dropdown */}
            <motion.div
              initial={{ opacity: 0, y: 8, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 8, scale: 0.96 }}
              transition={{ duration: 0.15, ease: 'easeOut' }}
              className="absolute top-full right-0 mt-2 w-52 bg-[#0d0d18]/98 backdrop-blur-2xl rounded-2xl shadow-2xl border border-white/15 z-[70] overflow-hidden p-1.5"
            >
              <div className="grid grid-cols-1 gap-1">
                {languages.map((language) => {
                  const Flag = getFlagByCode(language.code)
                  const isActive = i18n.language === language.code
                  return (
                    <motion.button
                      key={language.code}
                      type="button"
                      onClick={() => handleLanguageChange(language.code)}
                      className={`w-full flex items-center gap-3 px-3 py-2 text-left rounded-xl transition-all duration-150 ${
                        isActive ? 'bg-white/15 text-white font-bold' : 'hover:bg-white/5 text-white/70 hover:text-white'
                      }`}
                      whileHover={{ x: 3 }}
                    >
                      <span className="inline-flex items-center shadow-sm">
                        <Flag className="w-5 h-3.5 rounded-sm" />
                      </span>
                      <span className="flex-1 text-xs sm:text-sm font-medium">
                        {language.name}
                      </span>
                      {isActive && (
                        <motion.div
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          className="text-[#d4a843]"
                        >
                          <Check className="w-4 h-4" />
                        </motion.div>
                      )}
                    </motion.button>
                  )
                })}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  )
}
